import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Check, EyeOff, Trash2, Loader2, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { friendlyError } from "@/lib/errors";

// Formats "City, ST · relationship" once per testimonial row.
function formatLocation(t) {
  const loc = [t.city, t.state].filter(Boolean).join(", ");
  return t.relationship ? `${loc}${loc ? " · " : ""}${t.relationship}` : loc;
}

export default function AdminTestimonials() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending|published|all
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { published: filter === "published" };
      const { data } = await api.get("/admin/testimonials", { params });
      setRows(data);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const publish = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/testimonials/${id}/publish`);
      toast.success("Published — now visible on the landing page.");
      setRows((r) => r.map((row) => row.id === id ? { ...row, published: true } : row));
    } catch (err) {
      toast.error(friendlyError(err));
    } finally { setBusyId(null); }
  };
  const unpublish = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/testimonials/${id}/unpublish`);
      toast.success("Hidden from public view.");
      setRows((r) => r.map((row) => row.id === id ? { ...row, published: false } : row));
    } catch (err) {
      toast.error(friendlyError(err));
    } finally { setBusyId(null); }
  };
  const remove = async (id) => {
     
    if (!window.confirm("Permanently delete this testimonial?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/testimonials/${id}`);
      setRows((r) => r.filter((row) => row.id !== id));
      toast.success("Deleted.");
    } catch (err) {
      toast.error(friendlyError(err));
    } finally { setBusyId(null); }
  };

  if (user === null) return null;
  if (!user || user.role !== "admin") {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-6 py-14 text-center">
          <h1 className="font-display text-xl font-bold mb-2">Admin only</h1>
          <Link to="/" className="btn-primary inline-block mt-4">Back to home</Link>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl px-6 py-10" data-testid="admin-testimonials-page">
        <div className="flex items-start gap-3 mb-6">
          <div className="p-2 rounded-lg" style={{ background: "var(--ai-bg)" }}>
            <MessageSquareQuote className="w-6 h-6" style={{ color: "var(--ai-text)" }} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-extrabold">Testimonials</h1>
            <p className="text-sm max-w-2xl" style={{ color: "var(--text-secondary)" }}>
              Review submissions from users. Publish complimentary ones to feature them on the public homepage.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {[["pending", "Pending"], ["published", "Published"], ["all", "All"]].map(([k, label]) => (
            <button key={k} type="button"
                    onClick={() => setFilter(k)}
                    className={filter === k ? "btn-primary text-xs" : "btn-ghost text-xs"}
                    data-testid={`admin-testimonials-filter-${k}`}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm p-6 card-surface flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm p-6 card-surface text-center" style={{ color: "var(--text-secondary)" }}>
            No testimonials match this filter.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="admin-testimonials-list">
            {rows.map((t) => (
              <li key={t.id} className="card-surface p-5" data-testid={`admin-testimonial-${t.id}`}>
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-sm" style={{ color: "var(--text-primary)" }}>
                      {t.display_name}
                    </span>
                    <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                      {formatLocation(t)}
                    </span>
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className="w-3.5 h-3.5"
                              style={{ color: n <= (t.rating || 5) ? "#E5B93B" : "#E5E3DC",
                                       fill: n <= (t.rating || 5) ? "#F6C244" : "transparent" }} />
                      ))}
                    </div>
                    <span className={`pill ${t.published ? "pill-ok" : "pill-warn"}`}>
                      {t.published ? "Published" : "Pending"}
                    </span>
                    <span className="pill" style={{ background: "#F3F4F6", color: "#374151" }}>
                      {t.prompt_moment === "decided" ? "after decision" : "after filing"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {t.published ? (
                      <button type="button" onClick={() => unpublish(t.id)} disabled={busyId === t.id}
                              className="btn-ghost text-xs"
                              data-testid={`unpublish-${t.id}`}>
                        <EyeOff className="w-3.5 h-3.5 inline mr-1" /> Hide
                      </button>
                    ) : (
                      <button type="button" onClick={() => publish(t.id)} disabled={busyId === t.id}
                              className="btn-primary text-xs"
                              data-testid={`publish-${t.id}`}>
                        <Check className="w-3.5 h-3.5 inline mr-1" /> Publish
                      </button>
                    )}
                    <button type="button" onClick={() => remove(t.id)} disabled={busyId === t.id}
                            className="btn-ghost text-xs" title="Delete"
                            data-testid={`delete-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <p className="text-sm leading-relaxed italic" style={{ color: "var(--text-primary)" }}>
                  &ldquo;{t.quote}&rdquo;
                </p>
                <p className="text-[10.5px] mt-2" style={{ color: "var(--text-secondary)" }}>
                  Submitted {t.submitted_at?.slice(0, 10) || "—"}
                  {t.approved_by && ` · approved by ${t.approved_by}`}
                  {!t.consent_public && " · consent NOT given (do not publish)"}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  );
}
